/**
 * Validation functions for Step 2 (Admin Information)
 */
import { z } from "zod";
import type { CustomerAdminFormData } from "../utils/formatters";
import type { AccountManagerFormData } from "~/Pages/Tenants/types/tenants";

/**
 * Validate Step 2 form data
 * Returns field-level errors
 */
export function validateStep2(
  data: CustomerAdminFormData,
): Record<string, string[]> {
  const errors: Record<string, string[]> = {};

  try {
    const step2Schema = z.object({
      given_name: z
        .string()
        .min(1, "Given name is required")
        .min(2, "Given name must be at least 2 characters")
        .max(100, "Given name cannot exceed 100 characters")
        .refine(
          (val) => /^[a-zA-Z0-9\s\-_]+$/.test(val.trim()),
          "Given name cannot contain special characters",
        ),
      surname: z
        .string()
        .optional()
        .refine(
          (val) => !val || val.trim().length === 0 || val.trim().length >= 2,
          "Surname must be at least 2 characters if provided",
        )
        .refine(
          (val) =>
            !val ||
            val.trim().length === 0 ||
            /^[a-zA-Z0-9\s\-_]+$/.test(val.trim()),
          "Surname cannot contain special characters",
        ),
      other_name: z
        .string()
        .optional()
        .refine(
          (val) => !val || val.trim().length === 0 || val.trim().length >= 2,
          "Other name must be at least 2 characters if provided",
        )
        .refine(
          (val) =>
            !val ||
            val.trim().length === 0 ||
            /^[a-zA-Z0-9\s\-_]+$/.test(val.trim()),
          "Other name cannot contain special characters",
        ),
      preferred_name: z
        .string()
        .optional()
        .refine(
          (val) => !val || val.trim().length === 0 || val.trim().length >= 2,
          "Preferred name must be at least 2 characters if provided",
        )
        .refine(
          (val) =>
            !val ||
            val.trim().length === 0 ||
            /^[a-zA-Z0-9\s\-_]+$/.test(val.trim()),
          "Preferred name cannot contain special characters",
        ),
      contact_email: z
        .string()
        .min(1, "Contact email is required")
        .email("Invalid email format"),
      country: z
        .string()
        .min(1, "Country is required")
        .refine((val) => val.length === 2, "Country code must be 2 characters"),
      phone_code: z
        .string()
        .optional()
        .refine(
          (val) => !val || val.trim() === "" || /^\d+$/.test(val.trim()),
          "Phone code must be numeric",
        ),
      phone_number: z
        .string()
        .optional()
        .refine(
          (val) => !val || val.trim() === "" || /^\d+$/.test(val.trim()),
          "Phone number must be numeric",
        )
        .refine(
          (val) => !val || val.trim() === "" || val.trim().length >= 4,
          "Phone number must be at least 4 digits",
        ),
    });

    const result = step2Schema.safeParse({
      given_name: data.given_name,
      surname: data.surname,
      other_name: data.other_name,
      preferred_name: data.preferred_name,
      contact_email: data.contact_email.trim().toLowerCase(),
      country: data.country.toUpperCase(),
      phone_code: data.phone_code,
      phone_number: data.phone_number,
    });

    if (!result.success) {
      result.error.errors.forEach((error) => {
        const field = error.path[0] as string;
        if (!errors[field]) {
          errors[field] = [];
        }
        errors[field].push(error.message);
      });
    }
  } catch (error) {
    console.error("Validation error:", error);
  }

  return errors;
}

/**
 * Check if Step 2 is valid
 */
export function isStep2Valid(data: CustomerAdminFormData): boolean {
  const errors = validateStep2(data);
  return Object.keys(errors).length === 0;
}

/**
 * Validate Account Manager form data
 * Returns field-level errors
 */
export function validateAccountManager(
  data: AccountManagerFormData,
): Record<string, string[]> {
  const errors: Record<string, string[]> = {};

  try {
    // If adding a new account manager, email is required
    // If selecting an existing account manager, account_manager_id is required
    if (data.is_new_account_manager) {
      const newManagerSchema = z.object({
        account_manager_email: z
          .string()
          .min(1, "Email address is required")
          .email("Invalid email format"),
      });

      const result = newManagerSchema.safeParse({
        account_manager_email: data.account_manager_email.trim().toLowerCase(),
      });

      if (!result.success) {
        result.error.errors.forEach((error) => {
          const field = error.path[0] as string;
          if (!errors[field]) {
            errors[field] = [];
          }
          errors[field].push(error.message);
        });
      }
    } else {
      // Existing account manager selected - validate account_manager_id
      if (!data.account_manager_id || data.account_manager_id.trim() === "") {
        errors["account_manager_id"] = ["Account manager is required"];
      }
    }
  } catch (error) {
    console.error("Validation error:", error);
  }

  return errors;
}

/**
 * Check if Account Manager data is valid
 */
export function isAccountManagerValid(data: AccountManagerFormData): boolean {
  const errors = validateAccountManager(data);
  return Object.keys(errors).length === 0;
}
